import type { SetBLdMotivationKey } from '@shared/constants/setBLdMotivation';
import { SET_B_LD_MOTIVATION_KEYS } from '@shared/constants/setBLdMotivation';

/** localStorage draft for Set B /ldproductnow. */
export const LD_PRODUCT_NOW_STORAGE_KEY = 'ldsurvey:ldProductNow';

export type LdMotivationKey = SetBLdMotivationKey;

export type LdMotivations = Record<Exclude<LdMotivationKey, 'other'>, boolean> & {
  other: boolean;
  otherSpecify: string;
};

export type LdProductNowAnswers = {
  variant: string;
  motivations: LdMotivations;
  smokedOtherProduct: '' | 'yes' | 'no';
};

const MOTIVATION_OPTION_KEYS = SET_B_LD_MOTIVATION_KEYS.filter(
  (key): key is Exclude<LdMotivationKey, 'other'> => key !== 'other'
);

export const emptyLdMotivations = (): LdMotivations => ({
  productQuality: false,
  price: false,
  peerRecommendation: false,
  strongTaste: false,
  smoothDelivery: false,
  mentholFlavorExperience: false,
  other: false,
  otherSpecify: '',
});

export const emptyLdProductNowAnswers = (): LdProductNowAnswers => ({
  variant: '',
  motivations: emptyLdMotivations(),
  smokedOtherProduct: '',
});

function parseMotivations(value: unknown, fallbackOtherSpecify = ''): LdMotivations {
  const empty = emptyLdMotivations();
  if (typeof value !== 'object' || value === null) {
    return { ...empty, otherSpecify: fallbackOtherSpecify };
  }
  const o = value as Record<string, unknown>;
  return {
    productQuality: o.productQuality === true,
    price: o.price === true,
    peerRecommendation: o.peerRecommendation === true,
    strongTaste: o.strongTaste === true,
    smoothDelivery: o.smoothDelivery === true,
    mentholFlavorExperience: o.mentholFlavorExperience === true,
    other: o.other === true || (typeof o.otherSpecify === 'string' && o.otherSpecify.trim().length > 0),
    otherSpecify:
      typeof o.otherSpecify === 'string'
        ? o.otherSpecify
        : fallbackOtherSpecify,
  };
}

export function readLdProductNowAnswers(): LdProductNowAnswers {
  try {
    const raw = localStorage.getItem(LD_PRODUCT_NOW_STORAGE_KEY);
    if (!raw) return emptyLdProductNowAnswers();
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return emptyLdProductNowAnswers();
    const o = parsed as Record<string, unknown>;
    const smoked =
      o.smokedOtherProduct === 'yes' || o.smokedOtherProduct === 'no' ? o.smokedOtherProduct : '';

    let motivations = emptyLdMotivations();

    if (o.motivations != null && typeof o.motivations === 'object') {
      motivations = parseMotivations(o.motivations);
    } else if (
      typeof o.motivation === 'string' &&
      (SET_B_LD_MOTIVATION_KEYS as readonly string[]).includes(o.motivation)
    ) {
      // Back-compat: older single-select drafts.
      const key = o.motivation as LdMotivationKey;
      const otherSpecify =
        typeof o.motivationOtherSpecify === 'string' ? o.motivationOtherSpecify : '';
      if (key === 'other') {
        motivations = { ...emptyLdMotivations(), other: true, otherSpecify };
      } else {
        motivations = { ...emptyLdMotivations(), [key]: true, otherSpecify: '' };
      }
    }

    return {
      variant: typeof o.variant === 'string' ? o.variant : '',
      motivations,
      smokedOtherProduct: smoked,
    };
  } catch {
    return emptyLdProductNowAnswers();
  }
}

export function writeLdProductNowAnswers(data: LdProductNowAnswers): void {
  try {
    localStorage.setItem(LD_PRODUCT_NOW_STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export function clearLdProductNowAnswers(): void {
  try {
    localStorage.removeItem(LD_PRODUCT_NOW_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function getSelectedLdMotivations(
  answers: LdProductNowAnswers
): LdMotivationKey[] {
  const selected: LdMotivationKey[] = [];
  for (const key of MOTIVATION_OPTION_KEYS) {
    if (answers.motivations[key]) selected.push(key);
  }
  if (answers.motivations.other) selected.push('other');
  return selected;
}

export function hasLdMotivationSelected(answers: LdProductNowAnswers): boolean {
  const selected = getSelectedLdMotivations(answers);
  if (selected.length === 0) return false;
  if (answers.motivations.other && !answers.motivations.otherSpecify.trim()) return false;
  return true;
}

export function isLdProductNowComplete(): boolean {
  const data = readLdProductNowAnswers();
  if (!data.variant.trim()) return false;
  if (!hasLdMotivationSelected(data)) return false;
  return data.smokedOtherProduct === 'yes' || data.smokedOtherProduct === 'no';
}

export const LD_MOTIVATION_KEYS = [...SET_B_LD_MOTIVATION_KEYS] as LdMotivationKey[];
export const LD_MOTIVATION_OPTION_KEYS = MOTIVATION_OPTION_KEYS;
